import { useState, useEffect, useRef, useCallback, useId } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";
import addressBookService from "../../../services/addressBookService";
import AddEditContactModal from "../ContactModal/AddEditContactModal";
import useContactsPermissions from "../useContactsPermissions";
import history from "../../../history";
import { RouteName, DefaultLanguage } from "../../../constants/Constants";
import CustomToast from "../../UI/customToast";
import { announce } from "../../UI/LiveRegion";
import "./ContactPicker.scss";
import IconTooltip, { ICON_TOOLTIP_HOST } from "../../UI/Tooltip/IconTooltip";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const PICKER_LIMIT = 50;

const formatPhone = (phone, dialCode) => {
  if (!phone) return "";
  if (phone.startsWith("+")) return phone;
  const cleanDialCode = dialCode ? dialCode.replace(/^\+/, "") : "";
  return cleanDialCode ? `+${cleanDialCode} ${phone}` : phone;
};

const ContactPicker = ({ onSelectContact, selectedContact, onClearContact, currentPhone, currentDialCode, currentCountryCode, showEmail, pickerCache, onPickerCacheUpdate }) => {
  const { canAdd } = useContactsPermissions();
  const { createContactData } = useSelector((state) => state.addressBook);
  const langId = useSelector((state) => state.language?.selectedlanguage?.id);
  const [localisedValues, setLocalisedValues] = useState(Localize[langId] || Localize[DefaultLanguage]);
  const ariaLocalised = AriaLocalize[langId] || AriaLocalize['en_US'];

  useEffect(() => {
    if (langId) {
      setLocalisedValues(Localize[langId] || Localize[DefaultLanguage]);
    }
  }, [langId]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSelectedContact, setTempSelectedContact] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  // Picker contacts — managed locally (separate from main AddressBook redux state)
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentSkip, setCurrentSkip] = useState(0);

  const [tooltipInfo, setTooltipInfo] = useState(null);
  const tooltipBoxRef = useRef(null);
  const modalRef = useRef(null);
  const contactsListRef = useRef(null);
  const searchInputRef = useRef(null);
  const openerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const pendingAutoSelectId = useRef(null);
  const fetchingRef = useRef(false);
  const announceSearchRef = useRef(false);
  const dialogTitleId = useId();

  // Fetch contacts from GET /api/contacts/all
  const fetchContacts = useCallback(async (search, skip, append = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    try {
      const result = await addressBookService.getContactsForPicker({ search, skip, limit: PICKER_LIMIT });
      setContacts(prev => append ? [...prev, ...result.items] : result.items);
      setCurrentSkip(skip);
      setHasMore(skip + result.items.length < result.totalCount);
      if (announceSearchRef.current && skip === 0 && !append) {
        const count = result.totalCount || 0;
        if (announceSearchRef.current === "cleared") {
          announce((count === 1 ? ariaLocalised.one_contact_search_cleared : ariaLocalised.contacts_search_cleared).replace("{n}", count));
        } else {
          announce(count === 0 ? ariaLocalised.no_contacts_found : (count === 1 ? ariaLocalised.one_contact_found : ariaLocalised.contacts_found).replace("{n}", count));
        }
        announceSearchRef.current = false;
      }
      // Update shared cache only for the initial no-search fetch
      if (!search && skip === 0 && !append && onPickerCacheUpdate) {
        onPickerCacheUpdate({ items: result.items, totalCount: result.totalCount });
      }
    } catch (e) {
      if (e?.code === 1018) {
        history.replace(RouteName.SESSIONS);
        return;
      }
      // silent — keep existing list intact
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [onPickerCacheUpdate, ariaLocalised]);

  // Reset and load first page every time the modal opens
  useEffect(() => {
    if (isModalOpen) {
      announceSearchRef.current = false;
      setTempSelectedContact(selectedContact);
      setSearchQuery("");
      setHighlightedIndex(-1);
      if (pickerCache && pickerCache.items.length > 0) {
        // Use shared cache — no API call needed
        setContacts(pickerCache.items);
        setCurrentSkip(0);
        setHasMore(pickerCache.items.length < pickerCache.totalCount);
      } else {
        setContacts([]);
        setCurrentSkip(0);
        setHasMore(false);
        fetchContacts("", 0);
      }
    }
  }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced server-side search whenever the query changes (initial load is handled by the modal-open effect above)
  useEffect(() => {
    if (!isModalOpen) return;
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setContacts([]);
      setCurrentSkip(0);
      fetchContacts(searchQuery, 0);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll — append next page when list scrolls near the bottom
  const handleScroll = useCallback(() => {
    const el = contactsListRef.current;
    if (!el || isLoading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      fetchContacts(searchQuery, currentSkip + PICKER_LIMIT, true);
    }
  }, [isLoading, hasMore, currentSkip, searchQuery, fetchContacts]);

  useEffect(() => {
    const el = contactsListRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Reset highlighted index when the contacts list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [contacts]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && contactsListRef.current) {
      const items = contactsListRef.current.querySelectorAll(".picker-contact-item");
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Auto-select newly created contact once the refreshed list arrives
  useEffect(() => {
    if (pendingAutoSelectId.current && contacts.length > 0) {
      const newContact = contacts.find(
        (c) => String(c.contactId) === String(pendingAutoSelectId.current)
      );
      if (newContact) {
        setTempSelectedContact(newContact);
        pendingAutoSelectId.current = null;
      }
    }
  }, [contacts]);

  // Keyboard navigation inside the search input
  const handleSearchKeyDown = useCallback((e) => {
    if (!contacts.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < contacts.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : contacts.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && contacts[highlightedIndex]) {
        setTempSelectedContact(contacts[highlightedIndex]);
      }
    }
  }, [contacts, highlightedIndex]);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleTempSelectContact = (contact) => setTempSelectedContact(contact);

  const handleContinue = () => {
    if (tempSelectedContact) {
      onSelectContact(tempSelectedContact);
      setIsModalOpen(false);
    }
  };


  const handleOpenAddContact = () => setShowAddContactModal(true);

  const handleAddContactSuccess = () => {
    if (showAddContactModal) CustomToast.success(localisedValues['contact_added']);
    setShowAddContactModal(false);
    if (createContactData?.contactId) {
      pendingAutoSelectId.current = createContactData.contactId;
    }
    // Invalidate shared cache so next open re-fetches fresh data
    if (onPickerCacheUpdate) onPickerCacheUpdate(null);
    // Reload picker from first page so the new contact appears
    setContacts([]);
    setCurrentSkip(0);
    fetchContacts(searchQuery, 0);
  };

  const handleCloseAddContact = () => setShowAddContactModal(false);

  const handleOpenModal = (e) => {
    openerRef.current = e.currentTarget;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTempSelectedContact(null);
    setTooltipInfo(null);
  };

  // Focus the search input on open; restore focus to the opener on close
  useEffect(() => {
    if (isModalOpen) {
      const t = setTimeout(() => {
        if (!modalRef.current?.contains(document.activeElement)) searchInputRef.current?.focus();
      }, 300);
      return () => clearTimeout(t);
    }
    if (openerRef.current) {
      const el = openerRef.current;
      openerRef.current = null;
      // Restore focus asynchronously so the opener button is re-rendered first
      const t = setTimeout(() => el.focus?.(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isModalOpen]);

  // Esc-to-close + Tab focus trap within the dialog
  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleCloseModal();
        return;
      }
      if (e.key !== "Tab") return;
      const container = modalRef.current;
      if (!container) return;
      const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.getAttribute("aria-hidden") !== "true"
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  const showTooltipIfTruncated = (e, text, textEl) => {
    if (!textEl) return;
    // Disable tooltip on mobile/touch screens
    if (window.innerWidth <= 768) return;
    // scrollWidth > offsetWidth is reliable for white-space:nowrap + overflow:hidden spans
    if (textEl.scrollWidth <= textEl.offsetWidth) return;
    const modalEl = modalRef.current;
    if (!modalEl) return;
    const anchorRect = e.currentTarget.getBoundingClientRect();
    const modalRect = modalEl.getBoundingClientRect();
    const bottom = modalRect.bottom - anchorRect.top + 4;
    const tooltipMaxWidth = 320;
    const modalWidth = modalRect.width;
    // Center tooltip under the anchor element
    const anchorCenter = anchorRect.left - modalRect.left + anchorRect.width / 2;
    const idealLeft = anchorCenter - tooltipMaxWidth / 2;
    // Clamp so tooltip stays within modal bounds
    const left = Math.max(8, Math.min(idealLeft, modalWidth - tooltipMaxWidth - 8));
    // Initial arrowLeft — will be corrected by effect once actual tooltip width is known
    const arrowLeft = Math.max(8, anchorCenter - left - 5);
    setTooltipInfo({ text, bottom, left, arrowLeft, anchorCenter });
  };

  // After tooltip renders, clamp arrowLeft to the actual rendered tooltip width
  useEffect(() => {
    if (!tooltipInfo || !tooltipBoxRef.current) return;
    const actualWidth = tooltipBoxRef.current.offsetWidth;
    const corrected = Math.max(8, Math.min(tooltipInfo.anchorCenter - tooltipInfo.left - 5, actualWidth - 18));
    if (corrected !== tooltipInfo.arrowLeft) {
      setTooltipInfo(prev => prev ? { ...prev, arrowLeft: corrected } : null);
    }
  }, [tooltipInfo?.left, tooltipInfo?.bottom, tooltipInfo?.text]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNameMouseEnter = (e, name) => {
    const textEl = e.currentTarget.querySelector(".picker-contact-name-text");
    showTooltipIfTruncated(e, name, textEl);
  };

  const handleTooltipMouseLeave = () => setTooltipInfo(null);

  return (
    <div className="contact-picker">
      {/* Trigger / selected contact display */}
      {selectedContact ? (
        <div className="contact-picker-selected">
          <div className="selected-contact-info">
            <div className="selected-contact-avatar" aria-hidden="true">
              {getInitials(selectedContact.name)}
            </div>
            <div className="selected-contact-details">
              <span className="selected-contact-name">
                <span className="selected-contact-name-text">{selectedContact.name}</span>
                {selectedContact.isFavorite && (
                  <>
                    <span className="for-icon icon-fill-star" aria-hidden="true"></span>
                    <span className="visually-hidden">, {ariaLocalised.favorite}</span>
                  </>
                )}
              </span>
              {(showEmail ? selectedContact.email : selectedContact.phone) && <span className="visually-hidden">, </span>}
              <span className="selected-contact-phone">
                {showEmail
                  ? selectedContact.email || ""
                  : formatPhone(selectedContact.phone, selectedContact.dialCode)
                }
              </span>
            </div>
          </div>
          <div className="selected-contact-actions">
            <button
              type="button"
              className="btn btn-link btn-change"
              onClick={handleOpenModal}
            >
              {localisedValues['change']}
            </button>
            <button
              type="button"
              className="btn-clear-contact"
              onClick={onClearContact}
              aria-label={ariaLocalised.clear_selected_contact}
            >
              <span className="icon-close" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn-unstyled contact-picker-trigger"
          onClick={handleOpenModal}
        >
          <span className="trigger-avatar">
            <span className="for-icon icon-address-book" aria-hidden="true"></span>
          </span>
          <span className="trigger-content">
            <span className="trigger-title">{localisedValues['select_from_contacts']}</span>
            <span className="visually-hidden">, </span>
            <span className="trigger-subtitle">{localisedValues['choose_contact_desc']}</span>
          </span>
          <span className="trigger-arrow icon-arrow-right" aria-hidden="true"></span>
        </button>
      )}

      {/* Add New Contact Modal */}
      <AddEditContactModal
        isOpen={showAddContactModal}
        contact={null}
        onClose={handleCloseAddContact}
        onSuccess={handleAddContactSuccess}
      />

      {/* Picker modal */}
      {isModalOpen && (
        <>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="contact-picker-overlay" onClick={handleCloseModal} aria-hidden="true"></div>
          <div
            className="contact-picker-modal"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
          >
            {tooltipInfo && (
              <div className="picker-name-tooltip" ref={tooltipBoxRef} style={{ bottom: tooltipInfo.bottom, left: tooltipInfo.left }}>
                {tooltipInfo.text}
                <div className="picker-name-tooltip-arrow" style={{ left: tooltipInfo.arrowLeft }}></div>
              </div>
            )}
            <div className="contact-picker-modal-header">
              <h2 className="modal-title" id={dialogTitleId}>{localisedValues['select_contact_title']}</h2>
              <button type="button" className={`btn-close-modal ${ICON_TOOLTIP_HOST}`} onClick={handleCloseModal} aria-label={ariaLocalised.close}>
                <span className="icon-close" aria-hidden="true"></span>
                <IconTooltip label={ariaLocalised.close} />
              </button>
            </div>

            <div className="contact-picker-modal-body">
              <div className="picker-search">
                <span className="icon-search" aria-hidden="true"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={showEmail ? localisedValues['picker_search_email'] : localisedValues['picker_search_phone']}
                  value={searchQuery}
                  onChange={(e) => {
                    announceSearchRef.current = e.target.value === "" ? "cleared" : "search";
                    setSearchQuery(e.target.value);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  ref={searchInputRef}
                  aria-label={ariaLocalised.search_contacts}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn-clear-search"
                    onClick={() => {
                      announceSearchRef.current = "cleared";
                      setSearchQuery("");
                      searchInputRef.current?.focus();
                    }}
                    aria-label={ariaLocalised.clear_search}
                  >
                    <span className="icon-close" aria-hidden="true"></span>
                  </button>
                )}
              </div>

              <div className="picker-contacts-list custom-scroll-div" ref={contactsListRef}>
                {contacts.length === 0 && isLoading ? (
                  <div className="picker-loading">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    <span>{localisedValues['loading_contacts']}</span>
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="picker-empty">
                    {searchQuery
                      ? localisedValues['no_contacts_found_for'].replace('{value}', searchQuery)
                      : localisedValues['no_contacts_available']}
                  </div>
                ) : (
                  <>
                    {contacts.map((contact, index) => {
                      const isSelected = tempSelectedContact?.contactId === contact.contactId;
                      return (
                        <button
                          key={contact.contactId}
                          type="button"
                          className={clsx(
                            "btn-unstyled picker-contact-item",
                            isSelected && "selected",
                            highlightedIndex === index && "highlighted"
                          )}
                          onClick={() => handleTempSelectContact(contact)}
                          aria-pressed={isSelected}
                        >
                          <span className="picker-contact-checkbox">
                            {isSelected ? (
                              <span className="checkbox-checked">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                </svg>
                              </span>
                            ) : (
                              <span className="picker-contact-avatar" aria-hidden="true">
                                {getInitials(contact.name)}
                              </span>
                            )}
                          </span>
                          <span className="picker-contact-info">
                            <span
                              className="picker-contact-name"
                              onMouseEnter={(e) => handleNameMouseEnter(e, contact.name)}
                              onMouseLeave={handleTooltipMouseLeave}
                            >
                              <span className="picker-contact-name-text">{contact.name}</span>
                              {contact.isFavorite && (
                                <>
                                  <span className="for-icon icon-fill-star" aria-hidden="true"></span>
                                  <span className="visually-hidden">, {ariaLocalised.favorite}</span>
                                </>
                              )}
                            </span>
                            {(showEmail ? contact.email : contact.phone) && <span className="visually-hidden">, </span>}
                            <span className="picker-contact-phone">
                              {showEmail
                                ? contact.email || ""
                                : formatPhone(contact.phone, contact.dialCode)
                              }
                            </span>
                          </span>
                          {contact.company && (
                            <>
                              <span className="visually-hidden">, </span>
                              <span className="picker-contact-company">{contact.company}</span>
                            </>
                          )}
                        </button>
                      );
                    })}
                    {/* Inline loader shown while fetching the next page */}
                    {isLoading && (
                      <div className="picker-loading picker-loading-more">
                        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        <span>{localisedValues['loading_more']}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="contact-picker-modal-footer">
              {canAdd && (
                <div className="footer-top">
                  <button
                    type="button"
                    className="btn-add-new-contact"
                    onClick={handleOpenAddContact}
                  >
                    <span className="for-icon icon-plus-round" aria-hidden="true"></span>
                    {localisedValues['add_new_contact']}
                  </button>
                </div>
              )}
              <button
                type="button"
                className="btn btn-primary btn-continue"
                onClick={handleContinue}
                disabled={!tempSelectedContact}
              >
                {localisedValues['continue']}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

ContactPicker.propTypes = {
  onSelectContact: PropTypes.func.isRequired,
  selectedContact: PropTypes.shape({
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    dialCode: PropTypes.string,
    email: PropTypes.string,
    company: PropTypes.string,
    isFavorite: PropTypes.bool,
  }),
  onClearContact: PropTypes.func.isRequired,
  currentPhone: PropTypes.string,
  currentDialCode: PropTypes.string,
  currentCountryCode: PropTypes.string,
  showEmail: PropTypes.bool,
  pickerCache: PropTypes.shape({
    items: PropTypes.array,
    totalCount: PropTypes.number,
  }),
  onPickerCacheUpdate: PropTypes.func,
};

ContactPicker.defaultProps = {
  selectedContact: null,
  currentPhone: "",
  currentDialCode: "",
  currentCountryCode: "",
  showEmail: false,
  pickerCache: null,
  onPickerCacheUpdate: null,
};

export default ContactPicker;
